import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import View from '../common/View';
import Text from '../common/Text';
import Button from '../common/Button';
import './Register.css';

const Register: React.FC = () => {
  const { t } = useLanguage();
  const { register } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (formData.password !== formData.confirmPassword) {
      setError(t('PASSWORDS_DO_NOT_MATCH') || 'Passwords do not match');
      return;
    }
    if (formData.password.length < 8) {
      setError(
        t('PASSWORD_TOO_SHORT') || 'Password must be at least 8 characters',
      );
      return;
    }

    setLoading(true);
    try {
      // iOS Safari autofill commonly appends a trailing space and capitalises
      // the leading letter; backend rejects on email format. Normalise here.
      await register(
        formData.email.trim().toLowerCase(),
        formData.password,
        formData.firstName.trim(),
        formData.lastName.trim(),
      );
      navigate('/');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const onChange =
    (field: keyof typeof formData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData(prev => ({ ...prev, [field]: e.target.value }));
    };

  return (
    <View className="register">
      <View className="register-container">
        <View className="register-header">
          <Text variant="h1">{t('SIGN_UP') || 'Create account'}</Text>
          <Text variant="body">
            {t('CREATE_ACCOUNT_SUBTITLE') || 'Create your WunderGuide account'}
          </Text>
        </View>

        <form onSubmit={handleRegister} className="register-form">
          <View className="form-row">
            <View className="form-group">
              <Text variant="label">{t('FIRST_NAME') || 'First name'}</Text>
              <input
                type="text"
                value={formData.firstName}
                onChange={onChange('firstName')}
                className="form-input"
                autoComplete="given-name"
                required
                disabled={loading}
              />
            </View>
            <View className="form-group">
              <Text variant="label">{t('LAST_NAME') || 'Last name'}</Text>
              <input
                type="text"
                value={formData.lastName}
                onChange={onChange('lastName')}
                className="form-input"
                autoComplete="family-name"
                required
                disabled={loading}
              />
            </View>
          </View>

          <View className="form-group">
            <Text variant="label">{t('EMAIL') || 'Email'}</Text>
            <input
              type="email"
              value={formData.email}
              onChange={onChange('email')}
              className="form-input"
              placeholder="you@example.com"
              autoComplete="email"
              required
              disabled={loading}
            />
          </View>

          <View className="form-group">
            <Text variant="label">{t('PASSWORD') || 'Password'}</Text>
            <input
              type="password"
              value={formData.password}
              onChange={onChange('password')}
              className="form-input"
              placeholder="At least 8 characters"
              autoComplete="new-password"
              required
              disabled={loading}
            />
          </View>

          <View className="form-group">
            <Text variant="label">
              {t('CONFIRM_PASSWORD') || 'Confirm password'}
            </Text>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={onChange('confirmPassword')}
              className="form-input"
              autoComplete="new-password"
              required
              disabled={loading}
            />
          </View>

          {error && (
            <View className="signin-error">
              <Text variant="body">{error}</Text>
            </View>
          )}

          <Button
            type="submit"
            variant="primary"
            size="large"
            className="register-button"
            disabled={loading}
          >
            {loading
              ? t('CREATING_ACCOUNT') || 'Creating account…'
              : t('SIGN_UP') || 'Sign up'}
          </Button>
        </form>

        <View className="register-footer">
          <Text variant="body">
            {t('ALREADY_HAVE_ACCOUNT') || 'Already have an account?'}{' '}
            <Link to="/signin">{t('SIGN_IN') || 'Sign in'}</Link>
          </Text>
        </View>
      </View>
    </View>
  );
};

export default Register;
